import React, { useState, useEffect } from "react";
import { JhText } from "designPackages/textPackage";
import { Share2, Pen, Trash2, MoreVertical, Router } from "lucide-react";
import {
  JhDropdownMenu,
  JhDropdownMenuTrigger,
  JhDropdownMenuContent,
  JhDropdownMenuItem
} from "../../Tailwind/components/design-packages/dropDown";
import useModalStore from "../../zustandStores/useModalStore";
import JobService from "../../services/JobService";
import { JhUseToast } from "designPackages/toastPackage";
import LogService from "services/LogService";
import JobListEvents from "utils/Events/jobListEvents";
import { useTabStore } from "zustandStores/tabStore";
import TAB_VALUES from "./tabValues";
import { useIsMobile } from "hooks/useDeviceType";
import { jobPlanLabelMap, PREMIUM_JOB } from "services/constants";
import useJobOwnershipStore from "zustandStores/useJobOwnershipStore";

interface JobCardMenuProps {
  job: any;
  setCloseJobDrawer: (closeJobDrawer: boolean) => void;
  handleJobEdit: (
    event: React.MouseEvent<HTMLButtonElement>,
    type?: string
  ) => void;
}

const JobCardMenu: React.FC<JobCardMenuProps> = ({
  job,
  setCloseJobDrawer,
  handleJobEdit
}) => {
  const isOwned = useJobOwnershipStore(state => state.isJobOwned(job.job_id));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { setModalData } = useModalStore(state => ({
    setModalData: state.setModalData
  }));
  const { activeTab } = useTabStore(state => ({
    activeTab: state.activeTab
  }));
  const { toast } = JhUseToast();

  const isMobile = useIsMobile();
  const openWhatsAppShare = whatsappText => {
    const encodedText = encodeURIComponent(whatsappText);
    window.open(`https://wa.me/?text=${encodedText}`, "_blank");
  };

  const handleShareJob = async event => {
    // Close the menu first
    setIsMenuOpen(false);

    // Prevent event propagation
    event.preventDefault();
    event.stopPropagation();

    // Log share job event
    LogService.log(JobListEvents.JOB_CARD_MENU_CLICKED, {
      action: "share",
      job_id: job.job_id,
      job_title: job.job_title
    });

    try {
      // Get share data from API
      const shareData = await JobService.shareJob(job.job_id, "dashboard");

      // For mobile, directly open WhatsApp
      if (isMobile && shareData.whatsapp_share_text) {
        openWhatsAppShare(shareData.whatsapp_share_text);
        return;
      }

      // For desktop, open share modal
      setModalData({
        state: "open",
        type: "shareJobModal",
        data: {
          jobId: job.job_id,
          jobTitle: job.job_title,
          shareInfo: shareData,
          loading: false
        }
      });
    } catch (error) {
      console.error("Error sharing job:", error);
      toast({
        title: "Error",
        description: "Failed to generate sharing options",
        variant: "destructive"
      });
    }
  };

  const handleEditJob = event => {
    // Prevent event propagation to avoid issues
    event.preventDefault();
    event.stopPropagation();
    setIsMenuOpen(false);

    // Log edit job event
    LogService.log(JobListEvents.EDIT_JOB_CLICKED, {
      job_id: job.job_id,
      job_title: job.job_title,
      source: "job_card_menu"
    });

    handleJobEdit(event, "edit");
  };

  const handleCloseJob = event => {
    // Prevent event propagation to avoid issues
    event.preventDefault();
    event.stopPropagation();
    setIsMenuOpen(false);

    // Log close job event
    LogService.log(JobListEvents.JOB_CARD_MENU_CLICKED, {
      action: "close",
      job_id: job.job_id,
      job_title: job.job_title
    });

    setCloseJobDrawer(true);
    // Implement close job functionality
  };

  // Handle menu open/close changes
  const handleOpenChange = (open: boolean) => {
    // Log only when menu is being opened
    if (open && !isMenuOpen) {
      LogService.log(JobListEvents.JOB_CARD_MENU_CLICKED, {
        action: "open_menu",
        job_id: job.job_id,
        job_title: job.job_title
      });
    }
    setIsMenuOpen(open);
  };

  const handleMenuTriggerClick = e => {
    e.stopPropagation();
  };
  if (
    activeTab === TAB_VALUES.draft ||
    activeTab === TAB_VALUES.closed ||
    (activeTab === TAB_VALUES.review && !isOwned) ||
    (activeTab === TAB_VALUES.review && job.jobPlan === PREMIUM_JOB)
  )
    return null;
  return (
    <JhDropdownMenu open={isMenuOpen} onOpenChange={handleOpenChange}>
      <JhDropdownMenuTrigger className="hover:bg-muted" asChild>
        <button
          className="p-2 rounded-md border border-solid border-border"
          onClick={handleMenuTriggerClick}
        >
          <MoreVertical size={18} />
        </button>
      </JhDropdownMenuTrigger>
      <JhDropdownMenuContent align="end" alignOffset={-20}>
        {activeTab === TAB_VALUES.live && (
          <JhDropdownMenuItem
            className="cursor-pointer"
            onClick={handleShareJob}
          >
            <Share2 size={16} className="" />
            <JhText variant="body2" color="secondary-foreground">
              Share job
            </JhText>
          </JhDropdownMenuItem>
        )}
        {isOwned && activeTab === TAB_VALUES.live && (
          <JhDropdownMenuItem
            className="cursor-pointer"
            onClick={handleEditJob}
          >
            <Pen size={16} className="" />
            <JhText variant="body2" color="secondary-foreground">
              Edit job
            </JhText>
          </JhDropdownMenuItem>
        )}
        {isOwned && (
          <JhDropdownMenuItem
            className="cursor-pointer"
            onClick={handleCloseJob}
          >
            <Trash2 size={16} className="" />
            <JhText variant="body2" color="secondary-foreground">
              Close job
            </JhText>
          </JhDropdownMenuItem>
        )}
      </JhDropdownMenuContent>
    </JhDropdownMenu>
  );
};

export default JobCardMenu;
